import React, { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { MdSave } from "react-icons/md";
import { FiCopy } from "react-icons/fi";
import { toast } from "react-toastify";
import styles from "./StepSummary.module.scss";
import Axios from "_services/caller.service";

const StepSummary = ({ userData, onPrev, onSave, existingUser, triggerSave }) => {
  const { t } = useTranslation();
  const [copiedField, setCopiedField] = useState(null);

  useEffect(() => {
    if (!triggerSave) return;
    handleSave();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerSave]);

  // Normalize cabines from StepCabine:
  // userData.cabines = [{ name: "Cabine 1", type: "CONSULTING" }, ...]
  const cabinesNormalized = useMemo(() => {
    const arr = Array.isArray(userData?.cabines) ? userData.cabines : [];
    return arr
      .map((c) => ({
        cabname: (c?.name || "").trim(),
        cabtype: c?.type || "CONSULTING",
      }))
      .filter((c) => c.cabname.length > 0);
  }, [userData?.cabines]);

  // Helper pour formater le payload backend (multi-cabines)
  const formatPayload = (data) => {
    return {
      // Utilisateur
      login: data.login,
      password: data.password,
      email: data.email,
      name: data.name,
      lastName: data.lastName,

      // Abonnement
      subscriptionName: data.subscription,
      subscriptionVariation: data.plan,
      subid: data.subid, // N° Odoo

      // ✅ Cabines (multi)
      cabines: cabinesNormalized,

      // Groupe de permissions
      role_id: 2, // role "user" (owner classique)
      support_level_id: data.group || null,

      // Features
      features: data.features || "Dirac,AvoidAdminCheck",
    };
  };

  const handleSave = async () => {
    // validations simples
    if (!cabinesNormalized.length) {
      toast.error(t("common.no_cabins_provided"));
      console.error("❌ Aucune cabine renseignée");
      return;
    }

    const finalData = {
      ...userData,
      createdAt: new Date().toISOString(),
      isValid: true,
    };

    try {
      let response;

      if (existingUser) {
        // Mode liaison : relier un utilisateur existant
        const linkPayload = {
          userId: existingUser.id,
          subscriptionName: finalData.subscription,
          subscriptionVariation: finalData.plan,
          subid: finalData.subid,
          cabines: cabinesNormalized,
          features: finalData.features || "Dirac,AvoidAdminCheck",
        };

        console.group("🔗 Liaison utilisateur existant");
        console.log("Payload:", linkPayload);
        console.groupEnd();

        response = await Axios.post(`/linkExistingUserToSubscription`, linkPayload);
        toast.success(t("subscription.user_linked_success"));
      } else {
        // Mode création classique
        const payload = formatPayload(finalData);

        console.group("🚀 Données envoyées au backend");
        console.log("Payload:", payload);
        console.groupEnd();

        response = await Axios.post(`/createUserAndSubscription`, payload);
        toast.success(t("subscription.user_created_success"));
      }

      console.log("✅ Réponse backend:", response.data);
      onSave(finalData);
    } catch (err) {
      console.error("❌ Erreur lors de l'enregistrement:", err);
      
      const errorMessage = 
        err.response?.data?.error || 
        err.message || 
        t("subscription.creation_error");
      
      toast.error(errorMessage);
    }
  };

  const handleCopyPassword = async () => {
    try {
      await navigator.clipboard.writeText(userData.password);
      setCopiedField('password');
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error("Erreur copie:", err);
    }
  };

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText(userData.email);
      setCopiedField('email');
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error("Erreur copie:", err);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.summary}>
        {/* Utilisateur */}
        <h4>{existingUser ? t("subscription.existing_user_link") : t("subscription.user_info_title")}</h4>
        {existingUser ? (
          <>
            <p>
              <span className={styles.label}>ID:</span>{" "}
              <span className={styles.value}>{existingUser.id}</span>
            </p>
            <p>
              <span className={styles.label}>{t("common.lastname")}:</span>{" "}
              <span className={styles.value}>{existingUser.display_name}</span>
            </p>
            <p>
              <span className={styles.label}>Login:</span>{" "}
              <span className={styles.value}>{existingUser.login}</span>
            </p>
            <p>
              <span className={styles.label}>{t("common.email")}:</span>{" "}
              <span className={styles.value}>{existingUser.email}</span>
            </p>
          </>
        ) : (
          <>
        <p>
          <span className={styles.label}>Login:</span>{" "}
          <span className={styles.value}>{userData.login}</span>
        </p>
        <p>
          <span className={styles.label}>{t("common.email")}:</span>{" "}
          <span className={styles.emailContainer}>
            <span className={styles.emailValue}>{userData.email}</span>
            <button 
              className={`${styles.copyBtn} ${copiedField === 'email' ? styles.copied : ''} has-tooltip`}
              onClick={handleCopyEmail}
              type="button"
              aria-label={copiedField === 'email' ? t("common.copied") : t("common.copy_email")}
            >
              <FiCopy />
            </button>
          </span>
        </p>
        <p>
          <span className={styles.label}>{t("common.lastname")}:</span>{" "}
          <span className={styles.value}>{userData.lastName}</span>
        </p>
        <p>
          <span className={styles.label}>{t("common.firstname")}:</span>{" "}
          <span className={styles.value}>{userData.name}</span>
        </p>
        <p>
          <span className={styles.label}>{t("common.password")}:</span>{" "}
          <span className={styles.passwordContainer}>
            <span className={styles.passwordValue}>{userData.password}</span>
            <button
              className={`${styles.copyBtn} ${copiedField === 'password' ? styles.copied : ""} has-tooltip`}
              onClick={handleCopyPassword}
              type="button"
              aria-label={copiedField === 'password' ? t("common.copied") : t("common.copy_password")}
            >
              <FiCopy />
            </button>
          </span>
        </p>
          </>
        )}

        {/* Groupe */}
        {userData.groupName && (
          <p>
            <span className={styles.label}>{t("validation.group_label")}:</span>{" "}
            <span className={styles.value}>{userData.groupName}</span>
          </p>
        )}

        {/* Abonnement */}
        <h4>{t("subscription.sub_info_title")}</h4>
        <p>
          <span className={styles.label}>{t("subscription.label_label")}</span>{" "}
          <span className={styles.value}>{userData.subscription}</span>
        </p>
        <p>
          <span className={styles.label}>{t("subscription.odoo_label")}</span>{" "}
          <span className={styles.value}>{userData.subid}</span>
        </p>
        <p>
          <span className={styles.label}>{t("subscription.type_label")}</span>{" "}
          <span className={styles.value}>
            {userData.plan === "PRO" && t("subscription.plan_pro")}
            {userData.plan === "STANDARD" && t("subscription.plan_standard")}
            {userData.plan === "PLUS" && t("subscription.plan_plus")}
            {userData.plan === "DEVELOPER" && t("subscription.plan_developer")}
            {!["PRO", "STANDARD", "PLUS", "DEVELOPER"].includes(
              userData.plan,
            ) && userData.plan}
          </span>
        </p>

        {/* ✅ Cabines (visualisation multi) */}
        <h4>{t("subscription.cabines_section")}</h4>

        {cabinesNormalized.length === 0 ? (
          <p>
            <span className={styles.value}>{t("common.no_cabins_provided")}</span>
          </p>
        ) : (
          <ul className={styles.cabinesList}>
            {cabinesNormalized.map((c, idx) => (
              <li key={`${c.cabname}-${idx}`} className={styles.cabineItem}>
                <span className={styles.cabineIndex}>#{idx + 1}</span>
                <span className={styles.cabineName}>{c.cabname}</span>
                <span className={styles.cabineType}>{c.cabtype}</span>
              </li>
            ))}
          </ul>
        )}
      </div>


    </div>
  );
};

export default StepSummary;
